/**
 * FieldRenderer Component
 *
 * Renders a single field in the form builder canvas with edit mode controls.
 * Displays a preview of the field type and provides drag handle, edit, duplicate,
 * and delete actions.
 */

import { forwardRef, memo, useCallback } from 'react';
import { Box, Group, Text, ActionIcon, Tooltip, Paper, Divider } from '@mantine/core';
import { Icon } from '@/components/ui/icons/Icon';
import { FIELD_TYPE_CONFIGS, DND_ITEM_TYPE, DRAG_SOURCE } from './constants';
import { useFormBuilderStore } from '@/stores/formBuilderStore';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';
import type { FieldRendererProps, FormFieldDefinition } from './types';
import classes from './FormBuilder.module.css';

/**
 * Render a preview of the field based on its type
 */
const FieldPreview = memo(({ field }: { field: FormFieldDefinition }) => {
  const config = FIELD_TYPE_CONFIGS[field.type];
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Layout elements render differently
  if (config.isLayoutElement) {
    return <LayoutElementPreview field={field} />;
  }

  return (
    <Box className={classes.fieldPreviewContent}>
      {field.label && (
        <Text size="sm" fw={500} mb={4} style={accessibilityStyles}>
          {field.label}
          {field.required && <Text component="span" c="red.6" ml={2} style={accessibilityStyles}>*</Text>}
        </Text>
      )}
      {field.description && (
        <Text size="xs" c="dimmed" mb={8} style={accessibilityStyles}>
          {field.description}
        </Text>
      )}
      <FieldTypePreview field={field} />
    </Box>
  );
});

FieldPreview.displayName = 'FieldPreview';

/**
 * Preview for layout elements
 */
const LayoutElementPreview = memo(({ field }: { field: FormFieldDefinition }) => {
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  switch (field.type) {
    case 'heading':
      return (
        <Box className={classes.layoutPreview}>
          <Text
            size={field.headingLevel === 1 ? 'xl' : field.headingLevel === 2 ? 'lg' : 'md'}
            fw={600}
            style={accessibilityStyles}
          >
            {field.label || 'Section Heading'}
          </Text>
        </Box>
      );
    case 'paragraph':
      return (
        <Box className={classes.layoutPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>
            {field.content || 'Paragraph text content...'}
          </Text>
        </Box>
      );
    case 'divider':
      return (
        <Box py="md">
          <Divider />
        </Box>
      );
    case 'pagebreak':
      return (
        <Box py="sm">
          <Divider
            variant="dashed"
            labelPosition="center"
            label={
              <Group gap={4}>
                <Icon name="layers" size={12} />
                <Text size="xs" c="dimmed" style={accessibilityStyles}>
                  Page break{field.label ? ` — ${field.label}` : ''}
                </Text>
              </Group>
            }
          />
        </Box>
      );
    case 'spacer':
      return (
        <Box
          style={{ height: field.spacerHeight || 24 }}
          className={classes.spacerPreview}
        >
          <Text size="xs" c="dimmed" style={accessibilityStyles}>
            Spacer ({field.spacerHeight || 24}px)
          </Text>
        </Box>
      );
    default:
      return null;
  }
});

LayoutElementPreview.displayName = 'LayoutElementPreview';

/**
 * Preview for actual form fields (non-layout)
 */
const FieldTypePreview = memo(({ field }: { field: FormFieldDefinition }) => {
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  const placeholder = field.placeholder || 'Enter value...';

  switch (field.type) {
    case 'text':
    case 'email':
    case 'password':
    case 'number':
      return (
        <Box className={classes.inputPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
        </Box>
      );

    case 'textarea':
      return (
        <Box className={classes.textareaPreview} style={{ minHeight: (field.rows || 4) * 20 }}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
        </Box>
      );

    case 'select':
      return (
        <Box className={classes.selectPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
          <Icon name="chevronDown" size={16} />
        </Box>
      );

    case 'multiselect':
      return (
        <Box className={classes.selectPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
          <Icon name="chevronDown" size={16} />
        </Box>
      );

    case 'checkbox':
      return (
        <Group gap="xs">
          <Box className={classes.checkboxPreview} />
          <Text size="sm" style={accessibilityStyles}>{field.label}</Text>
        </Group>
      );

    case 'switch':
      return (
        <Group gap="xs">
          <Box className={classes.switchPreview} />
          <Text size="sm" style={accessibilityStyles}>{field.label}</Text>
        </Group>
      );

    case 'radio':
      return (
        <Box>
          {(field.options || []).slice(0, 3).map((opt, i) => (
            <Group key={i} gap="xs" mb={4}>
              <Box className={classes.radioPreview} />
              <Text size="sm" style={accessibilityStyles}>{opt.label}</Text>
            </Group>
          ))}
          {(field.options?.length || 0) > 3 && (
            <Text size="xs" c="dimmed" style={accessibilityStyles}>+{(field.options?.length || 0) - 3} more options</Text>
          )}
        </Box>
      );

    case 'date':
    case 'datetime':
    case 'time':
      return (
        <Box className={classes.inputPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
          <Icon name="calendar" size={16} />
        </Box>
      );

    case 'file':
    case 'image':
      return (
        <Box className={classes.filePreview}>
          <Icon name="upload" size={20} />
          <Text size="sm" c="dimmed" style={accessibilityStyles}>Click or drag to upload</Text>
        </Box>
      );

    case 'slider':
      return (
        <Box className={classes.sliderPreview}>
          <Box className={classes.sliderTrack}>
            <Box className={classes.sliderFill} style={{ width: '40%' }} />
            <Box className={classes.sliderThumb} style={{ left: '40%' }} />
          </Box>
          <Group justify="space-between" mt={4}>
            <Text size="xs" c="dimmed" style={accessibilityStyles}>{field.min ?? 0}</Text>
            <Text size="xs" c="dimmed" style={accessibilityStyles}>{field.max ?? 100}</Text>
          </Group>
        </Box>
      );

    case 'rating':
      return (
        <Group gap={4}>
          {Array.from({ length: field.max || 5 }).map((_, i) => (
            <Icon key={i} name="star" size={18} color={i < 3 ? 'gold' : 'gray'} />
          ))}
        </Group>
      );

    case 'color':
      return (
        <Group gap="xs">
          <Box
            className={classes.colorPreview}
            style={{ backgroundColor: (field.defaultValue as string) || '#3B82F6' }}
          />
          <Text size="sm" style={accessibilityStyles}>{(field.defaultValue as string) || '#3B82F6'}</Text>
        </Group>
      );

    case 'participants':
      return (
        <Box className={classes.textareaPreview}>
          <Group gap="xs" mb={4}>
            <Icon name="users" size={14} />
            <Text size="sm" c="dimmed" style={accessibilityStyles}>
              {(field.participantFields || []).map((f) => f.label).join(' · ') || 'Person details'}
            </Text>
          </Group>
          <Text size="xs" c="dimmed" style={accessibilityStyles}>
            {field.minItems ?? 0}–{field.maxItems ?? '∞'} people
            {field.relationshipOptions?.length ? ` · ${field.relationshipOptions.map((o) => o.label).join(', ')}` : ''}
          </Text>
        </Box>
      );

    case 'consent':
      return (
        <Group gap="xs" align="flex-start" wrap="nowrap">
          <Box className={classes.checkboxPreview} />
          <Text size="xs" c="dimmed" lineClamp={2} style={accessibilityStyles}>
            {field.consentText || 'Consent text'}{field.consentVersion ? ` (v${field.consentVersion})` : ''}
          </Text>
        </Group>
      );

    case 'address':
      return (
        <Box className={classes.textareaPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>Street · Suburb · City · Postal code</Text>
        </Box>
      );

    case 'location':
    case 'department':
      return (
        <Box className={classes.selectPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
          <Icon name="chevronDown" size={16} />
        </Box>
      );

    case 'quantity':
      return (
        <Box className={classes.inputPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>
            {field.min ?? 1}–{field.max ?? 10}{field.unitPrice ? ` × R${field.unitPrice}` : ''}
          </Text>
        </Box>
      );

    case 'richtext':
      return (
        <Box className={classes.richtextPreview}>
          <Group gap="xs" mb={8}>
            <Icon name="bold" size={14} />
            <Icon name="italic" size={14} />
            <Icon name="underline" size={14} />
            <Icon name="list" size={14} />
          </Group>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
        </Box>
      );

    default:
      return (
        <Box className={classes.inputPreview}>
          <Text size="sm" c="dimmed" style={accessibilityStyles}>{placeholder}</Text>
        </Box>
      );
  }
});

FieldTypePreview.displayName = 'FieldTypePreview';

/**
 * FieldRenderer Component
 */
const FieldRenderer = forwardRef<HTMLDivElement, FieldRendererProps>(
  ({ field, index, isSelected, isEditMode, onClick, className }, ref) => {
    const {
      selectField,
      removeField,
      duplicateField,
      setDraggedFieldId,
      setShowFieldSettings,
    } = useFormBuilderStore();

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const config = FIELD_TYPE_CONFIGS[field.type];

    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isEditMode) {
          selectField(field.id);
          onClick?.();
        }
      },
      [field.id, isEditMode, selectField, onClick]
    );

    const handleEdit = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        selectField(field.id);
        setShowFieldSettings(true);
      },
      [field.id, selectField, setShowFieldSettings]
    );

    const handleDuplicate = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        duplicateField(field.id);
      },
      [field.id, duplicateField]
    );

    const handleDelete = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        removeField(field.id);
      },
      [field.id, removeField]
    );

    const handleDragStart = useCallback(
      (e: React.DragEvent) => {
        if (!isEditMode) {
          e.preventDefault();
          return;
        }

        e.dataTransfer.setData(DND_ITEM_TYPE, JSON.stringify({
          source: DRAG_SOURCE.CANVAS,
          fieldId: field.id,
        }));
        e.dataTransfer.effectAllowed = 'move';

        setDraggedFieldId(field.id);

        // Add a slight delay to allow the drag image to be captured
        requestAnimationFrame(() => {
          (e.target as HTMLElement).classList.add(classes.dragging);
        });
      },
      [field.id, isEditMode, setDraggedFieldId]
    );

    const handleDragEnd = useCallback(
      (e: React.DragEvent) => {
        (e.target as HTMLElement).classList.remove(classes.dragging);
        setDraggedFieldId(null);
      },
      [setDraggedFieldId]
    );

    return (
      <Paper
        ref={ref}
        className={`${classes.fieldRenderer} ${isSelected ? classes.selected : ''} ${
          isEditMode ? classes.editable : ''
        } ${className || ''}`}
        onClick={handleClick}
        draggable={isEditMode}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        data-field-id={field.id}
        data-field-index={index}
        style={{
          gridColumn: field.colSpan ? `span ${field.colSpan}` : undefined,
        }}
      >
        {/* Field type indicator */}
        <Box className={classes.fieldTypeIndicator}>
          <Icon name={config.icon} size={14} />
          <Text size="xs" tt="uppercase" fw={500} style={accessibilityStyles}>
            {config.label}
          </Text>
        </Box>

        {/* Field content */}
        <Box className={classes.fieldContent}>
          <FieldPreview field={field} />
        </Box>

        {/* Edit mode controls */}
        {isEditMode && (
          <Group className={classes.fieldControls} gap={4}>
            <Tooltip label="Drag to reorder" position="top">
              <ActionIcon
                variant="subtle"
                size="sm"
                className={classes.dragHandle}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Icon name="gripVertical" size={14} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Edit field" position="top">
              <ActionIcon variant="subtle" size="sm" onClick={handleEdit}>
                <Icon name="edit" size={14} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Duplicate" position="top">
              <ActionIcon variant="subtle" size="sm" onClick={handleDuplicate}>
                <Icon name="copy" size={14} />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Delete" position="top">
              <ActionIcon variant="subtle" size="sm" color="red" onClick={handleDelete}>
                <Icon name="trash" size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        )}

        {/* Selection indicator */}
        {isSelected && <Box className={classes.selectionIndicator} />}
      </Paper>
    );
  }
);

FieldRenderer.displayName = 'FieldRenderer';

export default memo(FieldRenderer);
